import { useState, useEffect, useCallback } from 'react';
import { Bell, X } from 'lucide-react';

export default function ChangelogToast({ onOpenModal }) {
  const [show, setShow] = useState(false);
  const [changelog, setChangelog] = useState(null);

  const handleDismiss = useCallback(() => {
    if (changelog) {
      localStorage.setItem('lastSeenChangelogVersion', changelog.version);
    }
    setShow(false);
  }, [changelog]);

  useEffect(() => {
    // Fetch changelog from static file
    fetch('/changelog.json')
      .then(res => {
        if (!res.ok) {
          // Changelog file doesn't exist yet - this is fine
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (!data || !data.entries || data.entries.length === 0) {
          return;
        }

        setChangelog(data);

        // Check if user has seen this version
        const lastSeen = localStorage.getItem('lastSeenChangelogVersion');
        if (!lastSeen || lastSeen !== data.version) {
          setShow(true);
        }
      })
      .catch(err => {
        // Silently ignore - changelog is optional
        console.debug('Changelog not available:', err);
      });
  }, []);

  // Auto-dismiss after 5 seconds when toast is shown
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [show, handleDismiss]);

  const handleViewAll = () => {
    handleDismiss();
    if (onOpenModal) {
      onOpenModal();
    }
  };

  if (!show || !changelog || !changelog.entries[0]) return null;

  const latest = changelog.entries[0];

  // Badge styles by type
  const badgeStyles = {
    feature: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    fix: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    improvement: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    breaking: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
  };

  const badgeEmoji = {
    feature: '🎉',
    fix: '🐛',
    improvement: '⚡',
    breaking: '⚠️'
  };

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-4 animate-slide-in-right">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
          <Bell className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              New Update
            </h3>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${badgeStyles[latest.type]}`}>
              {badgeEmoji[latest.type]} {latest.type}
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
            {latest.message}
          </p>
          <button
            onClick={handleViewAll}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
          >
            See all changes →
          </button>
        </div>
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
